import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../api/client';
import {
  getCustomerFile,
  putCustomerProfile,
  type CustomerDetailsPatch,
  type CustomerProfileInput,
} from '../api/endpoints/customerFiles';
import { CustomerProfileSchema, type CustomerLevel, type CustomerPause, type CustomerProfile } from '../api/schemas';
import {
  LEVEL_LABEL,
  MAX_TAG_LENGTH,
  MAX_TAGS,
  fileVocabulary,
  hasTag,
  normalizeTags,
  toggleTag,
  type FileVocabulary,
} from '../lib/customerFile';
import { formatDateOnly } from '../lib/format';
import { useVersionConflict } from '../lib/use-version-conflict';
import { isVersionConflict } from '../lib/version-conflict';
import { customerFileKey, storeProfile } from '../lib/useCustomerProfiles';
import { useBusiness } from '../lib/useBusiness';
import { CustomerGlance, type GlanceDetails, type RosterMember } from './CustomerGlance';
import { CustomerNotes } from './CustomerNotes';
import { ErrorBanner } from './ErrorBanner';
import { LoadingState } from './LoadingState';

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

const SAVED_RECEIPT_MS = 2500;

const NO_ROSTER: readonly RosterMember[] = [];

/**
 * The team's ficha on one client: first what to know at a glance (level,
 * injuries, music, favorite coach, a travel pause), then tags in the vertical's
 * own words, then the notes thread. Everyone on the team reads and writes it;
 * the client never sees it.
 */
export function CustomerFile({ customerId }: { customerId: string }) {
  const business = useBusiness();
  const vocabulary = fileVocabulary(business?.category);
  // The business detail's roster: a front desk without the horario permission still reads it.
  const roster = business?.staff ?? NO_ROSTER;
  const fileQuery = useQuery({
    queryKey: customerFileKey(customerId),
    queryFn: () => getCustomerFile(customerId),
  });

  return (
    <section className="customer-file" aria-label="Ficha del equipo">
      <header className="customer-file-head">
        <h3 className="section-label">Ficha del equipo</h3>
        <p className="row-sub">Solo tu equipo la ve.</p>
      </header>
      {fileQuery.isLoading ? <LoadingState label="Cargando ficha…" /> : null}
      {fileQuery.isError ? (
        <div className="customer-file-error">
          <ErrorBanner message={loadMessage(fileQuery.error)} />
          <button type="button" className="btn small secondary" onClick={() => void fileQuery.refetch()}>
            Reintentar
          </button>
        </div>
      ) : null}
      {fileQuery.data ? (
        <>
          <ProfileEditor
            key={customerId}
            customerId={customerId}
            initial={fileQuery.data.profile}
            vocabulary={vocabulary}
            roster={roster}
          />
          <CustomerNotes customerId={customerId} notes={fileQuery.data.notes} vocabulary={vocabulary} />
        </>
      ) : null}
    </section>
  );
}

/**
 * Every change saves at once, one request at a time: a change while a save is
 * in flight is merged into the next one, so the server always ends on what the
 * screen shows even if someone taps three levels in a row or edits injuries
 * and music back to back. Level and tags ride on every save; a detail only on
 * its own, so merging keeps both. A failed save is what "Reintentar" resends.
 *
 * A 409 `version_conflict` is not a failure: `onConflict` answers it from what
 * the screen shows now (everything queued is part of that), either by queueing
 * the merged save, which goes out next with the fresh version, or by asking
 * the person, in which case nothing goes out until they choose.
 */
function useLatestSave(
  save: (input: CustomerProfileInput) => Promise<unknown>,
  onConflict: (error: unknown) => boolean,
) {
  const inFlight = useRef(false);
  const queued = useRef<CustomerProfileInput | null>(null);
  const failed = useRef<CustomerProfileInput | null>(null);
  const answerConflict = useRef(onConflict);
  answerConflict.current = onConflict;
  const [status, setStatus] = useState<SaveStatus>('idle');

  const run = useCallback(
    async (edit: CustomerProfileInput) => {
      if (inFlight.current) {
        queued.current = { ...queued.current, ...edit };
        return;
      }
      // A save that failed earlier rides along: details go only when they
      // change, so a later edit must carry it or it would be lost silently.
      const input: CustomerProfileInput = failed.current ? { ...failed.current, ...edit } : edit;
      inFlight.current = true;
      setStatus('saving');
      let next: CustomerProfileInput | null = input;
      let outcome: SaveStatus = 'saved';
      try {
        while (next) {
          const current: CustomerProfileInput = next;
          queued.current = null;
          try {
            await save(current);
            outcome = 'saved';
          } catch (error) {
            if (!isVersionConflict(error)) throw error;
            const later = queued.current;
            // What was queued meanwhile is on the screen, which is what the conflict reads.
            queued.current = null;
            if (!answerConflict.current(error)) {
              queued.current = later;
              throw error;
            }
            failed.current = null;
            // Nothing queued by the answer means it is asking (or found nothing left to send).
            if (!queued.current) outcome = 'idle';
          }
          next = queued.current;
        }
        failed.current = null;
        setStatus(outcome);
      } catch (error) {
        // The API refused the content itself (say, a coach no longer on the
        // team): resending it would fail forever and block every later edit.
        const refused = error instanceof ApiError && error.status === 400;
        failed.current = refused ? null : ({ ...next, ...queued.current } as CustomerProfileInput);
        queued.current = null;
        setStatus('error');
      } finally {
        inFlight.current = false;
      }
    },
    [save],
  );
  const retry = useCallback(() => {
    if (failed.current) void run(failed.current);
  }, [run]);
  // "Guardado" is a receipt, not a state: it fades back to nothing.
  useEffect(() => {
    if (status !== 'saved') return undefined;
    const timer = setTimeout(() => setStatus('idle'), SAVED_RECEIPT_MS);
    return () => clearTimeout(timer);
  }, [status]);
  return { run, retry, status };
}

function detailsOf(profile: CustomerProfile): GlanceDetails {
  return {
    injuries: profile.injuries,
    music: profile.music,
    favoriteStaffId: profile.favoriteStaffId,
    pause: profile.pause,
  };
}

/** What the ficha holds, in the units the server compares: the level, the tags as one list, and each detail. */
type ProfileDraft = {
  level: CustomerLevel | null;
  tags: string[];
  injuries: string | null;
  music: string | null;
  favoriteStaffId: string | null;
  pause: CustomerPause | null;
};
const PROFILE_DRAFT_KEYS = ['level', 'tags', 'injuries', 'music', 'favoriteStaffId', 'pause'] as const;
const DETAIL_KEYS = ['injuries', 'music', 'favoriteStaffId', 'pause'] as const;

function canonicalPause(pause: CustomerPause | null): CustomerPause | null {
  return pause ? { from: pause.from, until: pause.until, reason: pause.reason } : null;
}

function profileDraft(profile: CustomerProfile): ProfileDraft {
  return {
    level: profile.level,
    tags: profile.tags,
    injuries: profile.injuries,
    music: profile.music,
    favoriteStaffId: profile.favoriteStaffId,
    pause: canonicalPause(profile.pause),
  };
}

function parseProfileRecord(current: unknown): CustomerProfile | null {
  const parsed = CustomerProfileSchema.safeParse(current);
  return parsed.success ? parsed.data : null;
}

function sameJson(a: unknown, b: unknown): boolean {
  return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
}

/** The PUT body for a draft: level and tags always, and each detail that differs from `saved`. */
function inputFromDraft(draft: ProfileDraft, saved: ProfileDraft): CustomerProfileInput {
  const changed = Object.fromEntries(DETAIL_KEYS.filter((key) => !sameJson(draft[key], saved[key])).map((key) => [key, draft[key]]));
  return { level: draft.level, tags: draft.tags, ...changed };
}

function pauseText(pause: CustomerPause | null): string {
  return pause ? `${formatDateOnly(pause.from)} a ${formatDateOnly(pause.until)}` : 'Sin pausa';
}

function ProfileEditor({
  customerId,
  initial,
  vocabulary,
  roster,
}: {
  customerId: string;
  initial: CustomerProfile;
  vocabulary: FileVocabulary;
  roster: readonly RosterMember[];
}) {
  const queryClient = useQueryClient();
  const [level, setLevel] = useState<CustomerLevel | null>(initial.level);
  const [tags, setTags] = useState<string[]>(initial.tags);
  const [details, setDetails] = useState<GlanceDetails>(() => detailsOf(initial));
  const [draft, setDraft] = useState('');
  // What the ficha started from and its version: the read, then each save's answer. A newer copy arriving in
  // the background never moves them, so a save always names the version this person saw.
  const start = useRef<{ draft: ProfileDraft; version: number | undefined }>({
    draft: profileDraft(initial),
    version: initial.version,
  });
  // What the screen shows, readable from the async answer to a 409.
  const shown = useRef({ level, tags, details });
  shown.current = { level, tags, details };

  function startFrom(record: CustomerProfile) {
    start.current = { draft: profileDraft(record), version: record.version };
  }

  function draftNow(): ProfileDraft {
    const { level: shownLevel, tags: shownTags, details: shownDetails } = shown.current;
    return { level: shownLevel, tags: shownTags, ...shownDetails, pause: canonicalPause(shownDetails.pause) };
  }

  function showDraft(next: ProfileDraft) {
    const { level: nextLevel, tags: nextTags, ...nextDetails } = next;
    shown.current = { level: nextLevel, tags: nextTags, details: nextDetails };
    setLevel(nextLevel);
    setTags(nextTags);
    setDetails(nextDetails);
  }

  const save = useCallback(
    async (input: CustomerProfileInput) => {
      // The version is named here, at the moment of sending: a queued edit goes out with what the save before it left.
      const version = start.current.version;
      const saved = await putCustomerProfile(customerId, version !== undefined ? { ...input, expectedVersion: version } : input);
      startFrom(saved);
      storeProfile(queryClient, saved);
    },
    [customerId, queryClient],
  );

  const conflict = useVersionConflict<CustomerProfile, ProfileDraft>({
    keys: PROFILE_DRAFT_KEYS,
    labels: {
      level: 'Nivel',
      tags: vocabulary.tagsLabel,
      injuries: 'Lesiones',
      music: 'Música',
      favoriteStaffId: 'Coach favorito',
      pause: 'Pausa',
    },
    format: {
      level: (value) => (value === null ? 'Sin nivel' : LEVEL_LABEL[value]),
      tags: (value) => (value.length === 0 ? 'Vacío' : value.join(', ')),
      favoriteStaffId: (id) => (id === null ? 'Sin coach favorito' : (roster.find((member) => member.id === id)?.name ?? id)),
      pause: pauseText,
    },
    parse: parseProfileRecord,
    toDraft: profileDraft,
    rebase: ({ draft: merged, record }) => {
      showDraft(merged);
      startFrom(record);
      storeProfile(queryClient, record);
    },
    // Whatever goes out next is the merged ficha: what the other person saved plus what this one changed.
    save: ({ draft: merged }) => void run(inputFromDraft(merged, start.current.draft)),
    current: draftNow,
    discard: (record) => {
      showDraft(profileDraft(record));
      startFrom(record);
      storeProfile(queryClient, record);
    },
  });
  const { run, retry, status } = useLatestSave(save, (error) => conflict.handle(error, { base: start.current.draft, mine: draftNow() }));

  // While a clash waits for an answer the ficha still takes edits, but none goes out until the person chooses.
  const submit = (edit: CustomerProfileInput) => {
    if (!conflict.asking) void run(edit);
  };

  const commit = (nextLevel: CustomerLevel | null, nextTags: string[]) => {
    setLevel(nextLevel);
    setTags(nextTags);
    submit({ level: nextLevel, tags: nextTags });
  };

  /** One glance field changed: shown at once, saved beside the level and tags. */
  const commitDetail = (patch: CustomerDetailsPatch) => {
    setDetails((current) => ({ ...current, ...patch }));
    submit({ level, tags, ...patch });
  };

  const addDraft = (event: FormEvent) => {
    event.preventDefault();
    const clean = draft.trim();
    if (!clean) return;
    setDraft('');
    if (hasTag(tags, clean)) return;
    commit(level, normalizeTags([...tags, clean]));
  };

  const suggestions = vocabulary.tagSuggestions.filter((tag) => !hasTag(tags, tag));
  const full = tags.length >= MAX_TAGS;

  return (
    <>
      <CustomerGlance
        customerId={customerId}
        vocabulary={vocabulary}
        level={level}
        details={details}
        roster={roster}
        onLevel={(entry) => commit(level === entry ? null : entry, tags)}
        onSave={commitDetail}
      />
      <div className="group customer-file-card">
        <div className="customer-file-field">
          <p className="customer-file-label">{vocabulary.tagsLabel}</p>
          {tags.length > 0 ? (
            <ul className="customer-tags" aria-label={vocabulary.tagsLabel}>
              {tags.map((tag) => (
                <li key={tag}>
                  <button
                    type="button"
                    className="customer-tag"
                    data-selected="true"
                    aria-label={`Quitar ${tag}`}
                    onClick={() => commit(level, toggleTag(tags, tag))}
                  >
                    {tag}
                    <svg viewBox="0 0 12 12" aria-hidden="true">
                      <path d="M3.5 3.5l5 5m0-5l-5 5" />
                    </svg>
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
          {!full && suggestions.length > 0 ? (
            <ul className="customer-tags" aria-label="Sugerencias">
              {suggestions.map((tag) => (
                <li key={tag}>
                  <button type="button" className="customer-tag" onClick={() => commit(level, toggleTag(tags, tag))}>
                    <svg viewBox="0 0 12 12" aria-hidden="true">
                      <path d="M6 2.5v7M2.5 6h7" />
                    </svg>
                    {tag}
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
          {full ? (
            <p className="row-sub">Llegaste a {MAX_TAGS} etiquetas. Quita una para agregar otra.</p>
          ) : (
            <form className="customer-tag-add" onSubmit={addDraft}>
              <input
                className="field-input"
                value={draft}
                maxLength={MAX_TAG_LENGTH}
                placeholder="Otra etiqueta"
                aria-label={`Agregar a ${vocabulary.tagsLabel.toLowerCase()}`}
                onChange={(event) => setDraft(event.target.value)}
              />
              <button type="submit" className="btn small secondary" disabled={!draft.trim()}>
                Agregar
              </button>
            </form>
          )}
        </div>

        {/* A clash with somebody else's save, or the line that says both changes stayed. */}
        {conflict.notice}
        <SaveLine status={status} onRetry={retry} />
      </div>
    </>
  );
}

function SaveLine({ status, onRetry }: { status: SaveStatus; onRetry: () => void }) {
  if (status === 'error') {
    return (
      <p className="customer-file-save" role="alert" data-tone="danger">
        No se guardó el cambio.{' '}
        <button type="button" className="text-button" onClick={onRetry}>
          Reintentar
        </button>
      </p>
    );
  }
  return (
    <p className="customer-file-save" role="status" aria-live="polite" data-idle={status === 'idle' || undefined}>
      {status === 'saving' ? 'Guardando…' : status === 'saved' ? 'Guardado' : ''}
    </p>
  );
}

function loadMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return 'No pudimos cargar la ficha.';
}
